import { type TFunction } from 'i18next'
import { produce } from 'immer'
import * as R from 'remeda'

import {
  calcHeatmapRatio,
  type CellInfo,
  type DataDisplayType,
  exists,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  getSortIconBySortValue,
  mapFiveSegment,
  NumFormat,
  QuestionOp,
  type RowInfo,
  wrapFiveSegmentLabelMapWithTranslate,
} from '~/utils'

import {
  createFiveSegmentIndicatorLabelMap,
  type FiveSegmentColumnKey,
  fiveSegmentIndicators,
  type FiveSegmentsData,
} from './fiveSegments'
import { filterVisibleAnswersArrayData } from './visibleAnswersArrayData'

// Minimal interfaces using structural typing so both hooks and basic output can satisfy them
type AnswerData = FiveSegmentsData & {
  answerOption: { option: number; text: string }
  question: { question_id: string; question_text: string }
}

type CustomerAttributeData = FiveSegmentsData & {
  customerAttribute: { id: string; name: string }
  customerAttributeGroup: { name: string }
  number: number
}

type BuildFiveSegmentsTableInfoParams = {
  addHighlightProps: (key: string) => Record<string, unknown>
  answersArrayData: AnswerData[][]
  customerAttributesArrayData: CustomerAttributeData[][]
  dataDisplayType: DataDisplayType
  hiddenRowIds: string[]
  sampleSize: number
  sortValue: { ascending: boolean; columnKey: string } | null
  t: TFunction
  tamRatio: number
  totalData: FiveSegmentsData
}

export const buildFiveSegmentsTableInfo = ({
  addHighlightProps,
  answersArrayData,
  customerAttributesArrayData,
  dataDisplayType,
  hiddenRowIds,
  sampleSize,
  sortValue,
  t,
  tamRatio,
  totalData,
}: BuildFiveSegmentsTableInfoParams): RowInfo<FiveSegmentColumnKey>[] => {
  const fiveSegmentIndicatorLabelMap = createFiveSegmentIndicatorLabelMap(t)

  const visibleAnswersArrayData = filterVisibleAnswersArrayData(
    answersArrayData,
    hiddenRowIds,
  )
  const visibleCustomerAttributesArrayData = customerAttributesArrayData
    .map((customerAttributesData) =>
      customerAttributesData.filter(
        (customerAttributeData) =>
          !hiddenRowIds.includes(customerAttributeData.customerAttribute.id),
      ),
    )
    .filter((customerAttributesData) => 0 < customerAttributesData.length)

  const rowSpan =
    3 +
    visibleAnswersArrayData.length +
    visibleAnswersArrayData.flat().length +
    visibleCustomerAttributesArrayData.length +
    visibleCustomerAttributesArrayData.flat().length

  const titleRowInfo: RowInfo<FiveSegmentColumnKey> = {
    questionText: { isColumnHeader: true, colSpan: 3 },
    ...R.mapToObj(
      ['total', ...fiveSegments, ...fiveSegmentIndicators] as const,
      (indicator) => [
        indicator,
        {
          align: 'center',
          icon: getSortIconBySortValue(sortValue, indicator),
          isColumnHeader: true,
          isHeader: true,
          text: {
            total: t('overall'),
            ...wrapFiveSegmentLabelMapWithTranslate(
              fiveSegmentLabelI18nKeyMap,
              t,
            ),
            ...fiveSegmentIndicatorLabelMap,
          }[indicator],
        },
      ],
    ),
  }

  const indicatorMinMaxMap = R.mapToObj(fiveSegmentIndicators, (indicator) => {
    const ratioArray = R.concat(
      answersArrayData.flatMap((answersData) =>
        answersData.map((answerData) => answerData.ratios[indicator]),
      ),
      customerAttributesArrayData.flatMap((customerAttributesData) =>
        customerAttributesData.map(
          (customerAttributeData) => customerAttributeData.ratios[indicator],
        ),
      ),
    )

    return [
      indicator,
      { max: Math.max(...ratioArray), min: Math.min(...ratioArray) },
    ]
  })

  const getCountRowInfo = <T extends FiveSegmentsData>(
    dataArray: T[],
    data: T,
    index: number,
    getQuestionText: (data: T) => number | string,
    getAnswerNumber: (data: T, index: number) => number | string,
    getAnswerText: (data: T) => number | string,
  ): RowInfo<FiveSegmentColumnKey> => {
    const questionText =
      index === 0
        ? {
            isHeader: true,
            text: getQuestionText(data),
            rowSpan: dataArray.length,
          }
        : undefined

    const deriveCellIdentity = (indicator: string) =>
      `5seg-${getQuestionText(data)}--${getAnswerText(data)}--${indicator}`

    return {
      questionText,
      answerNumber: {
        isHeader: true,
        text: getAnswerNumber(data, index),
      },
      answerText: {
        isHeader: true,
        text: getAnswerText(data),
        textCharacterLimit: 30,
      },
      ...R.mapToObj(['total', ...fiveSegments] as const, (indicator) => {
        const ratio = data.ratios[indicator]
        const count = data.counts[indicator]
        const key = deriveCellIdentity(indicator)

        return [
          indicator,
          {
            ratio,
            text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
            ...addHighlightProps(key),
          } satisfies CellInfo,
        ]
      }),
      ...R.mapToObj(fiveSegmentIndicators, (indicator) => {
        const ratio = data.ratios[indicator]
        const count = data.counts[indicator]
        const key = deriveCellIdentity(indicator)
        const [text, copyValue] = NumFormat.countToPts(
          count,
          ratio,
          tamRatio,
          dataDisplayType,
        )
        const { max, min } = indicatorMinMaxMap[indicator]

        return [
          indicator,
          {
            copyValue,
            heatmap: true,
            ratio: calcHeatmapRatio(ratio, max, min),
            text,
            ...addHighlightProps(key),
          } satisfies CellInfo,
        ]
      }),
    }
  }

  return [
    // ヘッダー
    produce(titleRowInfo, (draft) => {
      for (const indicator of fiveSegmentIndicators) {
        const cellInfo = draft[indicator]
        if (exists(cellInfo)) {
          cellInfo.rowSpan = 3
        }
      }
      draft.loyalPad = { rowSpan }
      draft.generalPad = { rowSpan }
    }),
    // 分布
    {
      questionText: {
        colSpan: 3,
        isHeader: true,
        text: t('distribution'),
      },
      total: {
        text: NumFormat.forRatio(totalData.counts.total / sampleSize),
        ...addHighlightProps('5seg-total--distribution'),
      },
      ...mapFiveSegment((fiveSegment) => ({
        text: NumFormat.forRatio(totalData.ratios[fiveSegment]),
        ...addHighlightProps(`5seg-${fiveSegment}--distribution`),
      })),
    },
    // サンプル数
    {
      questionText: {
        colSpan: 3,
        isHeader: true,
        text: t('sample_size'),
      },
      total: {
        text: NumFormat.format(totalData.counts.total),
        ...addHighlightProps('5seg-total--sampleSize'),
      },
      ...mapFiveSegment((fiveSegment) => ({
        text: NumFormat.format(totalData.counts[fiveSegment]),
        ...addHighlightProps(`5seg-${fiveSegment}--sampleSize`),
      })),
    },
    // 設問
    ...visibleAnswersArrayData.flatMap((answersData) => [
      // 選択肢
      ...answersData.map((answerData, answerDataIndex) =>
        getCountRowInfo(
          answersData,
          answerData,
          answerDataIndex,
          ({ question }) => QuestionOp.toText(question),
          ({ answerOption }) => answerOption.option,
          ({ answerOption }) => answerOption.text,
        ),
      ),
      // 設問間のタイトル
      titleRowInfo,
    ]),
    // 顧客属性グループ
    ...visibleCustomerAttributesArrayData.flatMap((customerAttributesData) => [
      // 顧客属性
      ...customerAttributesData.map(
        (customerAttributeData, customerAttributeDataIndex) =>
          getCountRowInfo(
            customerAttributesData,
            customerAttributeData,
            customerAttributeDataIndex,
            ({ customerAttributeGroup }) => customerAttributeGroup.name,
            ({ number }) => number,
            ({ customerAttribute }) => customerAttribute.name,
          ),
      ),
      // 顧客属性間のタイトル
      titleRowInfo,
    ]),
  ]
}
